import { CourseDto, CourseDtoTypeEnum } from 'api/generated';
import { AppConfig } from 'config';

/**
 * The Reader (Textbook) app's origin. Every postMessage to the reader targets this exact origin —
 * never '*', which would leak the handoff token to any frame listening.
 */
export const READER_ORIGIN = AppConfig.READER_ORIGIN;

/** `read.traineracademy.org` → `traineracademy.org`. Two labels is enough for the hosts in play. */
const registrableDomain = (hostname: string): string => hostname.split('.').slice(-2).join('.');

/**
 * Whether the reader can work on the host we are being served from.
 *
 * The LMS runs one build across several brands, each on its own host
 * (`study.traineracademy.org`, `study.capitalprep.com`, `fly.pilotprep.co`), but the reader's
 * session cookie is only first-party — and its `frame-ancestors` only permissive — for a host
 * under its own registrable domain. Anywhere else the frame is guaranteed to fail, so the
 * Textbook entry point is hidden rather than shown broken.
 */
export const isReaderAvailable = (): boolean => {
  if (typeof window === 'undefined' || !READER_ORIGIN) {
    return false;
  }

  try {
    const { hostname } = window.location;

    // Dev is same-origin-ish by nature and the reader trusts localhost in a dev build.
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return true;
    }

    return registrableDomain(hostname) === registrableDomain(new URL(READER_ORIGIN).hostname);
  } catch (error) {
    return false;
  }
};

/**
 * Checks if a course is a certification course or certification bundle.
 */
export const isCertificationCourse = (course?: CourseDto | null): boolean => {
  if (!course) return false;
  const type = course.type as string;
  return (
    type === CourseDtoTypeEnum.Certificate ||
    type === CourseDtoTypeEnum.Bundle ||
    type === 'certificate' ||
    type === 'bundle'
  );
};

/**
 * Checks if the customer has any certification course or bundle on their account (trial or purchased).
 * If they only hold study material courses (type: 'default'), this returns false.
 */
export const hasCertificationCourseAccess = (
  courses?: CourseDto[] | null,
  activeCourse?: CourseDto | null,
): boolean => {
  if (courses && courses.length > 0) {
    return courses.some(isCertificationCourse);
  }
  return isCertificationCourse(activeCourse);
};

/**
 * Full visibility/entitlement gate for Textbook:
 * Host must support the Reader AND user must have at least one certification course/bundle.
 */
export const canUserAccessTextbook = (courses?: CourseDto[] | null, activeCourse?: CourseDto | null): boolean => {
  return isReaderAvailable() && hasCertificationCourseAccess(courses, activeCourse);
};

/** What `GET /reader/token` returns. */
export type ReaderTokenResponse = {
  token: string;
  tier: 'paid' | 'trial';
  courseId: string;
  courseIds: string[];
};

/**
 * Messages the reader sends up to us — every one it actually posts (`ready`, `session`,
 * `cap-hit`, `need-token`, `chrome`). Its type union also declares `navigate` and `resize`, but
 * no code path posts them, so there is nothing here to handle.
 */
export type ReaderInboundMessage =
  | { type: 'ipta-reader:ready'; embedded?: boolean }
  | { type: 'ipta-reader:need-token'; reason?: 'no-session' | 'expired' | 'new-window'; requestId?: string }
  | { type: 'ipta-reader:cap-hit'; tier?: string; used?: number; limit?: number; resetAt?: string | null }
  /**
   * The reader asks the host to hide/show its chrome (the LMS header) so mobile reading and the
   * AI tutor get the full viewport. The reader computes ONE desired state and emits only on
   * change, on phone viewports only — the LMS applies `collapse` verbatim; `reason` is
   * informational ("scroll" | "tutor").
   */
  | { type: 'ipta-reader:chrome'; collapse: boolean; reason?: 'scroll' | 'tutor' }
  | {
      type: 'ipta-reader:session';
      authenticated?: boolean;
      tier?: string;
      courseId?: string;
      courseIds?: string[];
      textbookId?: string;
      accentColor?: string;
    };

/**
 * The colour modes the reader accepts, lowercase and unquoted. `system` means "follow the device",
 * which the reader resolves with `prefers-color-scheme` and keeps tracking live. Anything else —
 * including a stray `'Dark'` or a boolean — is ignored: the reader keeps its current mode rather
 * than falling back to light, so a typo shows up as "nothing happens", not as a wrong colour.
 */
export type ReaderThemeMode = 'light' | 'dark' | 'system';

/** Messages we send down to the reader. */
export type ReaderOutboundMessage =
  | { type: 'ipta-reader:token'; token: string; requestId?: string }
  | { type: 'ipta-reader:set-course'; token?: string; textbookId?: string }
  // No `requestId`: unlike `ipta-reader:token`, theme messages are not correlated and get no ack.
  | { type: 'ipta-reader:theme'; mode: ReaderThemeMode }
  | { type: 'ipta-reader:logout' };

/**
 * Frames currently showing the reader. Populated by the Reader page so that a sign-out
 * anywhere in the LMS can reach a mounted reader without prop-drilling a ref.
 */
const mountedFrames = new Set<HTMLIFrameElement>();

export const registerReaderFrame = (frame: HTMLIFrameElement): (() => void) => {
  mountedFrames.add(frame);

  return () => {
    mountedFrames.delete(frame);
  };
};

export const postToReader = (frame: HTMLIFrameElement | null, message: ReaderOutboundMessage): void => {
  frame?.contentWindow?.postMessage(message, READER_ORIGIN);
};

/** Narrows an arbitrary window message to a reader message, origin checked. */
export const parseReaderMessage = (event: MessageEvent): ReaderInboundMessage | null => {
  const isLocal =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

  if (!isLocal && READER_ORIGIN && event.origin !== READER_ORIGIN) {
    return null;
  }

  const message = event.data;

  if (!message || typeof message !== 'object' || typeof (message as { type?: unknown }).type !== 'string') {
    return null;
  }

  return message as ReaderInboundMessage;
};

// How long to wait for a cold reader to boot and announce itself before giving up on the
// throwaway frame. Nothing is retried after this: silence means the reader never got far enough
// to have a listener, and a post into that gap would be dropped anyway.
const LOGOUT_FRAME_TIMEOUT_MS = 8000;

/**
 * Clears the reader's session on LMS sign-out.
 *
 * The reader session is 30 days and rolling, so without this a student stays signed into the
 * textbook on a shared computer long after they left the LMS.
 *
 * Sign-out rarely happens while the reader is on screen, so when no frame is mounted we mount a
 * throwaway one and wait for its `ipta-reader:ready` before posting. `ready` is the handshake:
 * the reader emits it from the same synchronous block that registers its message listener, so a
 * reply to it cannot arrive too early — which posting on the frame's `load` event genuinely could.
 * Both the book and the gate screen announce themselves, and the gate has no session to clear,
 * so either answer is correct.
 */
export const notifyReaderLogout = (): void => {
  if (!READER_ORIGIN) {
    return;
  }

  if (mountedFrames.size) {
    mountedFrames.forEach((frame) => postToReader(frame, { type: 'ipta-reader:logout' }));
    return;
  }

  if (typeof document === 'undefined' || !document.body) {
    return;
  }

  const frame = document.createElement('iframe');

  const cleanUp = () => {
    window.removeEventListener('message', onReady);
    window.clearTimeout(timer);
    frame.remove();
  };

  function onReady(event: MessageEvent) {
    // Only this frame's announcement — another reader frame would be handled by its own page.
    if (event.source !== frame.contentWindow) {
      return;
    }

    if (parseReaderMessage(event)?.type !== 'ipta-reader:ready') {
      return;
    }

    postToReader(frame, { type: 'ipta-reader:logout' });
    // Leave the frame alive briefly so the DELETE it triggers can go out.
    window.setTimeout(cleanUp, 1000);
  }

  const timer = window.setTimeout(cleanUp, LOGOUT_FRAME_TIMEOUT_MS);

  window.addEventListener('message', onReady);

  frame.setAttribute('aria-hidden', 'true');
  frame.setAttribute('tabindex', '-1');
  frame.setAttribute('title', 'reader sign-out');
  frame.style.display = 'none';
  frame.src = READER_ORIGIN;

  document.body.appendChild(frame);
};
